import { useEffect, useRef, useState } from "react";
import { BORDER_RADIUS, COLORS } from "../../constants/theme";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  placeholder: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  disabledTitle?: string;
  width?: string | number;
}

export function CustomSelect({
  placeholder,
  options,
  onChange,
  disabled = false,
  disabledTitle,
  width = "auto",
}: CustomSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    onChange(value);
    setOpen(false);
  };

  return (
    <div
      ref={ref}
      title={disabled ? disabledTitle : undefined}
      style={{ position: "relative", width, display: "inline-block" }}
    >
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          width: "100%",
          padding: "8px 32px 8px 12px",
          fontSize: "13px",
          borderRadius: BORDER_RADIUS.md,
          border: `1px solid ${open ? COLORS.primary.main : COLORS.border.medium}`,
          backgroundColor: COLORS.background.primary,
          color: COLORS.text.secondary,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          textAlign: "left",
          outline: "none",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {placeholder}
      </button>

      {/* Chevron */}
      <svg
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: open
            ? "translateY(-50%) rotate(180deg)"
            : "translateY(-50%)",
          pointerEvents: "none",
          transition: "transform 0.15s",
          opacity: disabled ? 0.5 : 1,
        }}
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
      >
        <path
          d="M2 4L6 8L10 4"
          stroke="#6b7280"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: 0,
            minWidth: "100%",
            backgroundColor: COLORS.background.primary,
            border: `1px solid ${COLORS.border.medium}`,
            borderRadius: BORDER_RADIUS.md,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          {options.map((opt) => (
            <div
              key={opt.value || opt.label}
              onMouseDown={() => !opt.disabled && handleSelect(opt.value)}
              style={{
                padding: "9px 14px",
                fontSize: "13px",
                color: opt.disabled ? COLORS.text.secondary : COLORS.text.primary,
                cursor: opt.disabled ? "default" : "pointer",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (!opt.disabled) (e.currentTarget as HTMLDivElement).style.backgroundColor =
                  COLORS.background.secondary;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.backgroundColor =
                  "transparent";
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
